import type { FleetV2Response, GetV5DevicesQueryParamsSortEnumKey } from '@emma/api_client/types';
import { Button, Flex, Menu, Portal, useToast } from '@lego/klik-ui';
import { ChevronDown } from '@lego/klik-ui/icons';
import { useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import {
  buildFilterParams,
  type FilterConfig,
  serverTableParams,
} from '@/hooks/common/serverTableParams';
import { useDevices } from '@/hooks/device';
import { useAssignDevicesToFleet } from '@/hooks/fleet/fleet_batch_assignment_resource';
import { useCanWriteById } from '@/hooks/workspace';
import { paths } from '@/utility/global_paths';
import { ToastOptions } from '@/utility/ToastOptions';
import { DeviceAssignPopover } from '../common/DeviceAssignPopover';
import { deviceTableColumns } from '../common/table/column_definitions/Device';
import { deviceEmptyMessage } from '../common/table/deviceEmptyMessage';
import { TableStoreProvider, useTableStore } from '../common/table/store/Context';
import { Table } from '../common/table/Table';
import { BatchUpdateCoreVersionModal } from './BatchUpdateCoreVersionModal';

type Props = {
  fleet: FleetV2Response;
};

const FILTER_DEBOUNCE_MS = 300;

const FILTERS: FilterConfig = {
  id: 'idIn',
  name: 'nameLike',
  macAddress: 'macAddressLike',
  reportedIp: 'reportedIpLike',
  workspace: 'workspaceIdIn',
  isConnected: 'isConnected',
  fleet: 'fleetIdIn',
  workcenter: 'workcenterIdLike',
  os: 'osNameLike',
  createdAt: ['createdAfter', 'createdBefore'],
  reportedCoreVersion: 'reportedCoreVersionLike',
  state: 'stateIn',
};

const fleetDevicesDescription =
  "Devices assigned to this fleet will receive the fleet's active deployment";

const FleetDeviceTableContent = ({ fleet }: Props) => {
  const canWrite = useCanWriteById(fleet.workspaceId);
  const toast = useToast();
  const [isCoreVersionModalOpen, setIsCoreVersionModalOpen] = useState(false);
  const { pagination, sorting, filters, rowSelection, setRowSelection } = useTableStore((s) => s);
  const [debouncedFilters] = useDebounceValue(filters, FILTER_DEBOUNCE_MS);
  const activeFilters = buildFilterParams(debouncedFilters, FILTERS);

  const selectedDeviceIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((k) => rowSelection[k])
        .map(Number),
    [rowSelection]
  );

  const queryParams = {
    ...serverTableParams<GetV5DevicesQueryParamsSortEnumKey>(pagination, sorting),
    ...activeFilters,
    fleetIdIn: [fleet.id],
  };

  const { data, isLoading, mutate, isValidating } = useDevices(queryParams);

  const { data: dataWorkspaceDevices, isLoading: workspaceDevicesLoading } = useDevices({
    workspaceIdIn: [fleet.workspaceId],
  });

  const { data: dataFleetTotal } = useDevices({
    fleetIdIn: [fleet.id],
    limit: 1,
    stateIn: ['PROVISIONED'],
  });
  const fleetTotalCount = dataFleetTotal?.itemCount ?? 0;

  const batchAssignDevices = useAssignDevicesToFleet();

  const fleetDevices = data?.devices ?? [];
  const workspaceDevices = dataWorkspaceDevices?.devices ?? [];

  const assignableDevices = useMemo(() => {
    const fleetDeviceIds = new Set(fleetDevices.map((d) => d.id));
    return workspaceDevices.filter((d) => !fleetDeviceIds.has(d.id)).sort((a, b) => a.id - b.id);
  }, [fleetDevices, workspaceDevices]);

  const handleAssign = async (
    deviceIds: number[]
  ): Promise<{ failedDeviceIds: number[] } | null> => {
    try {
      const result = await batchAssignDevices({
        fleetId: fleet.id,
        workspaceId: fleet.workspaceId,
        deviceIds,
      });
      const succeededCount = result.succeeded.length;
      const failedCount = result.failed.length;
      if (failedCount === 0) {
        toast(
          ToastOptions.success(
            'Devices assigned',
            `Successfully assigned ${succeededCount} device${succeededCount !== 1 ? 's' : ''} to fleet`
          )
        );
      } else {
        toast(
          ToastOptions.error(
            'Partial assignment',
            `${succeededCount} assigned, ${failedCount} failed: ${result.failed.map((f) => `Device #${f.deviceId}: ${f.error}`).join('; ')}`
          )
        );
      }
      void mutate();
      return { failedDeviceIds: result.failed.map(({ deviceId }) => deviceId) };
    } catch {
      return null;
    }
  };

  const totalItems = data?.itemCount || 0;
  const pageCount = Math.ceil(totalItems / pagination.pageSize);

  return (
    <>
      <Table
        addButton={
          canWrite
            ? () => (
                <Flex gap={2}>
                  <Menu>
                    <Menu.Button
                      as={Button}
                      rightIcon={<ChevronDown />}
                      size="sm"
                      variant="outline"
                    >
                      Batch Actions
                    </Menu.Button>
                    <Portal>
                      <Menu.List>
                        <Menu.Item
                          isDisabled={fleetTotalCount === 0}
                          onClick={() => setIsCoreVersionModalOpen(true)}
                        >
                          Update Core Version
                          {selectedDeviceIds.length > 0 && ` (${selectedDeviceIds.length})`}
                        </Menu.Item>
                      </Menu.List>
                    </Portal>
                  </Menu>
                  <DeviceAssignPopover
                    assignableDevices={assignableDevices}
                    isLoadingDevices={workspaceDevicesLoading}
                    onAssign={handleAssign}
                    tooltipLabel="Assign devices to the fleet"
                  />
                </Flex>
              )
            : undefined
        }
        columns={deviceTableColumns}
        data={fleetDevices}
        description={fleetDevicesDescription}
        emptyMessage={deviceEmptyMessage(
          data?.itemCount,
          'No devices in this fleet',
          Object.keys(activeFilters).length > 0
        )}
        enableRowSelection={canWrite}
        getRowCanSelect={(row) => row.original.state === 'PROVISIONED'}
        getRowId={(row) => String(row.id)}
        isLoading={isLoading}
        isValidating={isValidating}
        refresh={mutate}
        rowLink={({ id }) => paths.deviceById(id)}
        serverSide={{ pageCount, rowCount: totalItems }}
        title="Devices"
      />
      <BatchUpdateCoreVersionModal
        deviceCount={selectedDeviceIds.length}
        deviceIds={selectedDeviceIds.length > 0 ? selectedDeviceIds : undefined}
        fleetId={fleet.id}
        isOpen={isCoreVersionModalOpen}
        onClose={() => setIsCoreVersionModalOpen(false)}
        onSuccess={() => {
          setRowSelection({});
          void mutate();
        }}
        workspaceId={fleet.workspaceId}
      />
    </>
  );
};

export const FleetDeviceTable = ({ fleet }: Props) => (
  <TableStoreProvider initialSorting={[{ desc: true, id: 'id' }]} name={`FleetDevices-${fleet.id}`}>
    <FleetDeviceTableContent fleet={fleet} />
  </TableStoreProvider>
);
